import { WEB_APP_URL } from './api';
import { getCurrentTouch, trackPublicAttributionEvent } from './marketingAttribution';

export function classifyCta(href) {
  let url;
  try { url = new URL(href, window.location.origin); } catch { return null; }
  if (url.hostname === 'play.google.com') return { eventName: 'google_play_click', destination: 'google_play', store: 'google_play' };
  if (url.hostname === 'apps.apple.com') return { eventName: 'app_store_click', destination: 'app_store', store: 'app_store' };
  if (url.hostname === 'wa.me' || url.hostname === 'api.whatsapp.com') return { eventName: 'whatsapp_direct_click', destination: 'whatsapp_direct' };
  if (url.hostname === 'chat.whatsapp.com' || url.hostname === 'whatsapp.com') return { eventName: 'whatsapp_community_click', destination: 'whatsapp_community' };
  if (url.origin === new URL(WEB_APP_URL).origin || ['app.checkstall.com', 'test.app.checkstall.com'].includes(url.hostname) ||
      (url.hostname === 'localhost' && url.port === '8081')) return { eventName: 'web_app_click', destination: 'web_app' };
  if (![window.location.hostname, 'checkstall.com', 'www.checkstall.com'].includes(url.hostname)) return null;
  const pathname = url.pathname.replace(/\/+$/, '') || '/';
  const names = { '/download': 'download_cta_click', '/downloads': 'download_cta_click',
    '/start-importing': 'start_importing_click', '/how-to-use': 'how_to_use_click' };
  return names[pathname] ? { eventName: names[pathname], destination: pathname } : null;
}

// Capture-phase delegation observes the originating route before React navigates.
// It leaves existing link decoration, form behavior and navigation untouched.
export function installCtaTracking() {
  const onClick = (event) => {
    if (event.type === 'auxclick' ? event.button !== 1 : event.button !== 0) return;
    const target = event.target.closest?.('a[href],button[data-track-event]');
    if (!target) return;
    let details = target.href ? classifyCta(target.href) : null;
    if (target.dataset.trackEvent) details = { eventName: target.dataset.trackEvent, destination: target.dataset.destination || 'process' };
    if (!details && target.href && /get started/i.test(target.textContent)) {
      details = { eventName: 'get_started_click', destination: new URL(target.href).pathname };
    }
    if (!details) return;
    const { eventName, ...properties } = details;
    const location = target.closest('[data-cta-location]')?.dataset.ctaLocation ||
      (target.closest('nav,header') ? 'navigation' : target.closest('footer') ? 'footer' : 'content');
    trackPublicAttributionEvent(eventName, { ...properties, cta_location: location, trigger: 'manual' });
  };
  document.addEventListener('click', onClick, true);
  document.addEventListener('auxclick', onClick, true);
  return () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('auxclick', onClick, true);
  };
}

// A fresh instance represents one page visit. Durations are deltas, not repeated totals.
export function startPageBehavior() {
  const touch = getCurrentTouch(); // Preserve the departing page's attribution on SPA cleanup.
  const reached = new Set();
  const viewedSections = new Set();
  let active = !document.hidden && document.hasFocus();
  let away = false;
  let last = performance.now();
  let pending = 0;
  let total = 0;
  let scrollTimer;
  let sectionObserver;
  const sample = () => {
    const now = performance.now();
    if (active) { const elapsed = Math.max(0, now - last); pending += elapsed; total += elapsed; }
    last = now;
  };
  const flush = (reason) => {
    sample();
    const delta = Math.floor(pending);
    if (delta < 1000) return;
    pending -= delta;
    trackPublicAttributionEvent('page_engagement', {
      engaged_ms: delta, total_engaged_ms: Math.floor(total), flush_reason: reason,
    }, { touch });
  };
  const checkScroll = () => {
    scrollTimer = undefined;
    if (document.hidden || away) return;
    const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
    if (!height) return;
    const percent = Math.min(100, 100 * (window.scrollY + window.innerHeight) / height);
    for (const milestone of [25, 50, 75, 90]) {
      if (percent >= milestone && !reached.has(milestone)) {
        reached.add(milestone);
        trackPublicAttributionEvent('scroll_depth', { percent_scrolled: milestone }, { touch });
      }
    }
  };
  const scheduleScroll = () => {
    if (scrollTimer === undefined) scrollTimer = window.setTimeout(checkScroll, 150);
  };
  const updateActive = () => {
    sample();
    active = !away && !document.hidden && document.hasFocus();
    if (document.hidden) flush('hidden');
    else scheduleScroll();
  };
  const blur = () => { sample(); active = false; };
  const hide = () => { flush('pagehide'); active = false; away = true; };
  const show = () => { away = false; updateActive(); };
  const interval = window.setInterval(() => flush('interval'), 30000);
  const sectionMarkers = document.querySelectorAll('[data-section-view]');
  if ('IntersectionObserver' in window && sectionMarkers.length) {
    sectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const eventName = entry.target.dataset.sectionView;
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || !eventName || viewedSections.has(eventName)) continue;
        viewedSections.add(eventName);
        trackPublicAttributionEvent(eventName, { section: eventName.replace(/_view$/, '') }, { touch });
        sectionObserver.unobserve(entry.target);
      }
    }, { threshold: 0.5, rootMargin: '0px 0px -10% 0px' });
    sectionMarkers.forEach((marker) => sectionObserver.observe(marker));
  }
  window.addEventListener('scroll', scheduleScroll, { passive: true });
  window.addEventListener('resize', scheduleScroll);
  window.addEventListener('focus', updateActive);
  window.addEventListener('blur', blur);
  window.addEventListener('pagehide', hide);
  window.addEventListener('pageshow', show);
  document.addEventListener('visibilitychange', updateActive);
  scheduleScroll();
  return () => {
    flush('route_exit');
    window.clearInterval(interval);
    window.clearTimeout(scrollTimer);
    sectionObserver?.disconnect();
    window.removeEventListener('scroll', scheduleScroll);
    window.removeEventListener('resize', scheduleScroll);
    window.removeEventListener('focus', updateActive);
    window.removeEventListener('blur', blur);
    window.removeEventListener('pagehide', hide);
    window.removeEventListener('pageshow', show);
    document.removeEventListener('visibilitychange', updateActive);
  };
}
